export type AnimalSexType = 'm' | 'bull' | 'steer' | 'f' | 'heifer' | 'cow';

export function calculateYieldHelper(
  liveWeight: number,
  hangingWeight: number,
) {
  const yieldValue = liveWeight === 0 ? 0 : hangingWeight / liveWeight;

  return `${(yieldValue * 100).toFixed(2)}%`;
}

export interface AnimalHeadHangingWeightData {
  hangingWeight: number;
  isHangingWeightPerSide?: boolean;
  hangingWeightSideA?: number;
  hangingWeightSideB?: number;
}

/**
 * Calculates the hanging weight for a specific split based on the animal head's
 * hanging weight data, split type, split part, and split index.
 * @param animalHead - The animal head containing hanging weight data
 * @param splitType - The split type of the entire animal (e.g., 'whole', 'half', 'quarters', 'half_and_two_quarters')
 * @param splitPart - The part type of this specific split (e.g., 'whole', 'half', 'quarter')
 * @param splitIndex - The index of this split within the animal head's cutsheet information array
 * @returns The calculated hanging weight for this split
 *
 * @example
 * // For a half split with per-side weights
 * calculateSplitHangingWeight(
 *   { hangingWeight: 600, isHangingWeightPerSide: true, hangingWeightSideA: 310, hangingWeightSideB: 290 },
 *   'half',
 *   'half',
 *   0
 * ) // Returns 310 (SideA)
 *
 * @example
 * // For quarters with per-side weights
 * calculateSplitHangingWeight(
 *   { hangingWeight: 600, isHangingWeightPerSide: true, hangingWeightSideA: 310, hangingWeightSideB: 290 },
 *   'quarters',
 *   'quarter',
 *   2
 * ) // Returns 145 (half of SideB, since index 2 is in the second pair)
 *
 * @example
 * // For half_and_two_quarters where index 0 is half, indexes 1-2 are quarters
 * calculateSplitHangingWeight(
 *   { hangingWeight: 600, isHangingWeightPerSide: true, hangingWeightSideA: 310, hangingWeightSideB: 290 },
 *   'half_and_two_quarters',
 *   'half',
 *   0
 * ) // Returns 310 (first half is SideA)
 */
export function calculateSplitHangingWeight(
  animalHead: AnimalHeadHangingWeightData,
  splitType: string,
  splitPart: string,
  splitIndex: number,
): number {
  const isPerSide = animalHead.isHangingWeightPerSide ?? false;
  const sideAWeight = animalHead.hangingWeightSideA ?? 0;
  const sideBWeight = animalHead.hangingWeightSideB ?? 0;

  const baseHangingWeight = isPerSide
    ? (sideAWeight || 0) + (sideBWeight || 0)
    : (animalHead.hangingWeight ?? 0);

  const lowerSplitPart = splitPart.toLowerCase();

  // Handle 'whole' split part
  if (lowerSplitPart === 'whole') {
    return baseHangingWeight;
  }

  // Handle 'half' split part
  if (lowerSplitPart.startsWith('half')) {
    if (isPerSide) {
      // For half_and_two_quarters: index 0 is the first half (SideA)
      // For regular half: index 0 = SideA, index 1 = SideB
      return splitIndex === 0 ? sideAWeight : sideBWeight;
    }
    return baseHangingWeight * 0.5;
  }

  // Handle 'quarter' split part
  if (lowerSplitPart.startsWith('quarter')) {
    if (isPerSide) {
      if (splitType === 'half_and_two_quarters') {
        // For half_and_two_quarters:
        // index 0 = half (handled above)
        // index 1 & 2 = quarters from the second half (SideB)
        // Each quarter is half of SideB
        return sideBWeight * 0.5;
      }

      // For regular quarters:
      // Quarters 0-1 are from SideA, Quarters 2-3 are from SideB
      // Each quarter is half of its respective side
      const sideWeight = splitIndex < 2 ? sideAWeight : sideBWeight;
      return sideWeight * 0.5;
    }

    return baseHangingWeight * 0.25;
  }

  // Fallback: return full hanging weight for unknown split parts
  return baseHangingWeight;
}

export const getSplitMultiplier = (splitPart: string) => {
  const lowerSplitPart = splitPart.toLowerCase();

  if (lowerSplitPart.startsWith('half')) return 0.5;

  if (lowerSplitPart.startsWith('quarter')) return 0.25;

  return 1;
};

/**
 * The per-slot identifiers a buyer's portion is billed and printed under. A
 * split's letter is its 0-based position among its head's live splits, so the
 * first slot is A, the second B, and so on.
 */
export const SPLIT_LETTERS = ['A', 'B', 'C', 'D'] as const;

/**
 * The identifier letter for a split's slot, or undefined when the slot can't be
 * resolved (rebuilt/stale splits) or falls outside a whole animal's four slots.
 *
 * Callers must stay quiet on undefined rather than defaulting — a portion that
 * claims another buyer's letter is worse than one carrying no letter at all
 * (SUP-329, where a stale index printed every half as Side A).
 */
export function splitSlotLetter(
  slotIndex: number | undefined,
): string | undefined {
  if (slotIndex == null) return undefined;
  return SPLIT_LETTERS[slotIndex];
}

/** A physical side of the carcass, as printed on carcass tags. */
export type CarcassSide = 'A' | 'B';

/**
 * The physical side a split was cut from, mirroring the mapping the invoice
 * bills on (calculateSplitHangingWeight) so a buyer's cutsheet, their invoice
 * and the carcass tag's QR all agree about which side they got:
 * - half: slot 0 → Side A, slot 1 → Side B
 * - quarter: slots 0-1 → Side A, slots 2-3 → Side B
 * - half_and_two_quarters: slot 0 is the half (Side A), so both quarters
 *   (slots 1-2) are cut from Side B
 *
 * `slotIndex` is the split's 0-based position among its head's LIVE splits in
 * `_id` order. Returns undefined for any slot that doesn't map to a side — the
 * caller must stay quiet rather than guess (SUP-329).
 */
export function resolveSplitSide(
  splitPart: string | null | undefined,
  splitType: string | null | undefined,
  slotIndex: number | undefined,
): CarcassSide | undefined {
  if (slotIndex == null) return undefined;
  if (splitPart === 'half') {
    if (slotIndex === 0) return 'A';
    if (slotIndex === 1) return 'B';
    return undefined;
  }
  if (splitPart !== 'quarter') return undefined;

  if (splitType === 'half_and_two_quarters') {
    return slotIndex === 1 || slotIndex === 2 ? 'B' : undefined;
  }
  if (slotIndex < 0 || slotIndex > 3) return undefined;
  return slotIndex < 2 ? 'A' : 'B';
}
